import { z } from 'zod';
import { GrainSchema, SectionKeySchema, SectionsSchema } from './catalog.js';
import { CellSchema, IdSchema, PublicationIdSchema, ResultIdSchema } from './common.js';

/** Limite de período: ano, mês ou dia. */
const PeriodBoundSchema = z.string().regex(/^\d{4}(-(0[1-9]|1[0-2])(-(0[1-9]|[12]\d|3[01]))?)?$/, 'use AAAA, AAAA-MM ou AAAA-MM-DD');

/*
 * Entradas e saídas das ferramentas do MCP.
 * Contratos 4 (conversa e esclarecimento), 5 (texto da resposta pronto) e 6 (feedback) estão aqui.
 * Tudo que chega do agente é texto não confiável: limites de tamanho em todo campo.
 */

const ParamsSchema = z.record(IdSchema, z.string().min(1).max(200));

/** Nome de um campo do modelo do app, como describe_app devolve em modelo.campos. Conferido no modelo, nunca vira texto livre. */
const ModelFieldNameSchema = z.string().min(1).max(256);
/** Id de uma dimensão do catálogo ou nome de um campo do modelo do app. */
const DimensionOrFieldSchema = z.string().min(1).max(256);

// ---------- search_context ----------

export const SearchContextInputSchema = z.object({
  question: z.string().min(1).max(1000).describe('Pergunta ou assunto em linguagem natural'),
  limit: z.number().int().min(1).max(10).default(3).describe('Máximo de assuntos devolvidos'),
});
export type SearchContextInput = z.infer<typeof SearchContextInputSchema>;

export const SearchContextOutputSchema = z.object({
  publication_id: PublicationIdSchema,
  subjects: z.array(
    z.object({
      id: IdSchema,
      nome: z.string(),
      descricao: z.string().optional(),
      metricas: z.array(
        z.object({ id: IdSchema, nome: z.string(), descricao: z.string(), unidade: z.string(), sinonimos: z.array(z.string()) }),
      ),
      dimensoes: z.array(
        z.object({ id: IdSchema, nome: z.string(), tipo: z.string(), sinonimos: z.array(z.string()), sensivel: z.boolean() }),
      ),
      queries_validadas: z.array(
        z.object({
          id: IdSchema,
          exemplo: z.string(),
          descricao: z.string().optional(),
          certificada: z.boolean(),
          parametros: z.array(z.object({ nome: IdSchema, dimensao: IdSchema, obrigatorio: z.boolean() })),
        }),
      ),
      roteiros: z.array(z.object({ id: IdSchema, quando: z.string(), passos: z.array(z.string()) })),
    }),
  ),
});
export type SearchContextOutput = z.infer<typeof SearchContextOutputSchema>;

// ---------- list_values ----------

export const ListValuesInputSchema = z.object({
  subject_id: IdSchema.describe('Id do assunto, como devolvido por search_context'),
  dimension_id: DimensionOrFieldSchema.describe('Id da dimensão dentro do assunto, ou nome de um campo de describe_app.modelo'),
  search: z.string().max(100).optional().describe('Trecho para filtrar os valores'),
  limit: z.number().int().min(1).max(100).default(20),
});
export type ListValuesInput = z.infer<typeof ListValuesInputSchema>;

export const ListValuesOutputSchema = z.object({
  subject_id: IdSchema,
  dimension_id: z.string(),
  values: z.array(z.string()),
  truncated: z.boolean(),
});
export type ListValuesOutput = z.infer<typeof ListValuesOutputSchema>;

// ---------- run_validated_query ----------

export const RunValidatedQueryInputSchema = z.object({
  query_id: IdSchema.describe('Id da query validada'),
  params: ParamsSchema.default({}).describe('Parâmetros da query, por nome. Períodos mensais no formato AAAA-MM'),
});
export type RunValidatedQueryInput = z.infer<typeof RunValidatedQueryInputSchema>;

export const ColumnSchema = z.object({
  id: z.string(),
  name: z.string(),
  kind: z.enum(['dimension', 'measure']),
  unit: z.string().optional(),
  masked: z.boolean().optional(),
  /** O que o Qlik calculou: o campo da dimensão ou a expressão da medida. Vai para os detalhes da fonte. */
  expression: z.string().max(4000).optional(),
  /** O que a medida significa, em palavras de gente: a descrição do contexto ou a frase do item do modelo. Vai ao lado da fórmula. */
  description: z.string().max(2000).optional(),
});
export type Column = z.infer<typeof ColumnSchema>;

/** Resultado certificado de uma query validada. */
export const QueryResultSchema = z.object({
  result_id: ResultIdSchema,
  /** A query tem certificação registrada no catálogo e foi executada sem desvio. */
  certified: z.boolean(),
  query_id: IdSchema,
  query_version: z.number().int(),
  subject_id: IdSchema,
  params: z.record(z.string(), z.string()),
  columns: z.array(ColumnSchema),
  rows: z.array(z.array(CellSchema)),
  row_count: z.number().int(),
  total_rows: z.number().int(),
  truncated: z.boolean(),
  /** Contrato 5: texto pronto, no idioma da instância. O agente cita sem alterar. */
  answer_text: z.string(),
  /** Avisos que o agente repassa ao usuário (ex.: itens fora do catálogo curado, ADR 0037). */
  caveats: z.array(z.string()).optional(),
  /** Modo privacidade do cliente dono do app (ADR 0047): os valores aparecem só para a pessoa e nunca vão para uma LLM. */
  privacy_mode: z.boolean().optional(),
  source: z.object({ id: IdSchema, app: z.string(), last_reload_at: z.string().nullable() }),
  publication_id: PublicationIdSchema,
  executed_at: z.string(),
});
export type QueryResult = z.infer<typeof QueryResultSchema>;

// ---------- query_metric (v1) ----------

/** Prefixo do query_id de consultas montadas fora das queries validadas. */
export const ADHOC_QUERY_PREFIX = 'adhoc-';

const QueryFiltersSchema = z
  .record(DimensionOrFieldSchema, z.union([z.string().min(1).max(200), z.array(z.string().min(1).max(200)).min(1).max(20)]))
  .default({})
  .describe(
    'Filtros por id de dimensão ou nome de campo do modelo; uma lista vira "ou". Períodos mensais em AAAA-MM; datas em AAAA, AAAA-MM, AAAA-MM-DD ou intervalo 2025-01..2025-06. ' +
      'Condição sobre os valores do campo: "<0", ">=1000", "<=6", "<=$(vMesBase)" (variável de describe_app.modelo.variaveis). Começa com: "Sim*".',
  );

const QueryPeriodSchema = z
  .object({
    dimension_id: IdSchema.describe('Id de uma dimensão do tipo data'),
    grain: GrainSchema.optional().describe('Agrupa por dia, mes, trimestre ou ano. Conta como uma das duas dimensões'),
    from: PeriodBoundSchema.optional().describe('Início, inclusive: AAAA, AAAA-MM ou AAAA-MM-DD'),
    to: PeriodBoundSchema.optional().describe('Fim, inclusive: AAAA, AAAA-MM ou AAAA-MM-DD'),
  })
  .describe('Período sobre uma dimensão de data: agrupar por grão e/ou filtrar por intervalo');

export const QueryMetricInputSchema = z.object({
  subject_id: IdSchema.describe('Id do assunto'),
  metric_id: IdSchema.describe('Id da métrica do catálogo'),
  group_by: z.array(IdSchema).max(2).default([]).describe('Até duas dimensões de agrupamento, por id'),
  filters: QueryFiltersSchema,
  period: QueryPeriodSchema.optional(),
  limit: z.number().int().min(1).max(1000).default(50),
});
export type QueryMetricInput = z.infer<typeof QueryMetricInputSchema>;

// ---------- consulta universal: list_apps, describe_app, query ----------

/** A pergunta do usuário como o agente entendeu. Obrigatória nas ferramentas novas: vai para a auditoria. */
const QuestionSchema = z.string().min(1).max(1000).describe('Pergunta do usuário, como você entendeu. Fica registrada na auditoria');

export const ListAppsInputSchema = z.object({ question: QuestionSchema });
export type ListAppsInput = z.infer<typeof ListAppsInputSchema>;

export const ListAppsOutputSchema = z.object({
  publication_id: PublicationIdSchema,
  apps: z.array(
    z.object({
      id: IdSchema,
      nome: z.string(),
      descricao: z.string().optional(),
      fonte: IdSchema,
      app: z.string(),
      metricas: z.number().int(),
      dimensoes: z.number().int(),
      queries_validadas: z.number().int(),
      /** Primeiras linhas da seção contexto. */
      contexto: z.string().optional(),
    }),
  ),
});
export type ListAppsOutput = z.infer<typeof ListAppsOutputSchema>;

export const DescribeAppInputSchema = z.object({
  question: QuestionSchema,
  subject_id: IdSchema.describe('Id do app, como devolvido por list_apps'),
});
export type DescribeAppInput = z.infer<typeof DescribeAppInputSchema>;

export const DescribeAppOutputSchema = z.object({
  publication_id: PublicationIdSchema,
  id: IdSchema,
  nome: z.string(),
  descricao: z.string().optional(),
  fonte: IdSchema,
  app: z.string(),
  /** Só as seções preenchidas. */
  secoes: SectionsSchema,
  regras_de_uso: z.array(z.string()),
  dimensoes: z.array(
    z.object({ id: IdSchema, nome: z.string(), tipo: z.string(), sinonimos: z.array(z.string()), descricao: z.string().optional(), sensivel: z.boolean() }),
  ),
  metricas: z.array(z.object({ id: IdSchema, nome: z.string(), descricao: z.string(), unidade: z.string(), sinonimos: z.array(z.string()) })),
  queries_validadas: z.array(
    z.object({
      id: IdSchema,
      exemplo: z.string(),
      descricao: z.string().optional(),
      certificada: z.boolean(),
      metricas: z.array(IdSchema),
      dimensoes: z.array(IdSchema),
      periodo: z.object({ dimensao: IdSchema, grao: GrainSchema }).optional(),
      parametros: z.array(z.object({ nome: IdSchema, dimensao: IdSchema, obrigatorio: z.boolean() })),
    }),
  ),
  roteiros: z.array(z.object({ id: IdSchema, quando: z.string(), passos: z.array(z.string()) })),
  /**
   * Modelo do app no Qlik, só o que o catálogo não cobre (ADR 0037): campos e itens mestres que query aceita
   * fora do catálogo, com resultado não certificado. Ausente quando o recurso está desligado ou a fonte não lê o modelo.
   */
  modelo: z
    .object({
      campos: z.array(z.object({ nome: z.string(), tipo: z.enum(['data', 'numero', 'texto']), distintos: z.number().int() })),
      medidas_mestras: z.array(z.object({ id: z.string(), nome: z.string(), descricao: z.string().optional() })),
      dimensoes_mestras: z.array(z.object({ id: z.string(), nome: z.string(), campo: z.string() })),
      /** Variáveis do app (nome e definição), para condição de filtro como {"Mês": "<=$(vMesBase)"}. */
      variaveis: z.array(z.object({ nome: z.string(), definicao: z.string() })).optional(),
      /** Havia mais campos do que o teto; ficaram os de data e os de menos valores distintos. */
      truncado: z.boolean(),
    })
    .optional(),
});
export type DescribeAppOutput = z.infer<typeof DescribeAppOutputSchema>;

export const SortDirectionSchema = z.enum(['asc', 'desc']);

/** Agregações que o servidor escreve sobre um campo do modelo. A LLM escolhe a peça; a expressão é do servidor. */
export const ModelAggregationSchema = z.enum(['contar', 'contar_distintos', 'somar', 'media', 'minimo', 'maximo']);
export type ModelAggregation = z.infer<typeof ModelAggregationSchema>;

/** Medida fora do catálogo: uma medida mestra do app, ou uma agregação sobre um campo do modelo. */
export const ModelMeasureRefSchema = z.union([
  z.object({ mestre: z.string().min(1).max(256).describe('Id de describe_app.modelo.medidas_mestras') }).strict(),
  z
    .object({
      agregacao: ModelAggregationSchema,
      campo: ModelFieldNameSchema.describe('Nome de describe_app.modelo.campos'),
    })
    .strict(),
]);
export type ModelMeasureRef = z.infer<typeof ModelMeasureRefSchema>;

/** Razão entre duas medidas (percentual, participação, variação): o servidor escreve (A) / (B) e o Qlik calcula. */
export const RatioRefSchema = z
  .object({
    razao: z
      .object({
        numerador: z.union([IdSchema, ModelMeasureRefSchema]).describe('Id do catálogo, {mestre} ou {agregacao, campo}'),
        denominador: z.union([IdSchema, ModelMeasureRefSchema]).describe('Id do catálogo, {mestre} ou {agregacao, campo}'),
      })
      .strict(),
  })
  .strict();
export type RatioRef = z.infer<typeof RatioRefSchema>;

/** Dimensão fora do catálogo: um campo do modelo; grain só em campo de data. */
export const ModelDimensionRefSchema = z
  .object({
    campo: ModelFieldNameSchema.describe('Nome de describe_app.modelo.campos'),
    grain: GrainSchema.optional().describe('Só para campo do tipo data: dia, mes, trimestre ou ano'),
  })
  .strict();
export type ModelDimensionRef = z.infer<typeof ModelDimensionRefSchema>;

export const QueryInputSchema = z.object({
  question: QuestionSchema,
  subject_id: IdSchema.describe('Id do app (assunto)'),
  metric_ids: z
    .array(z.union([IdSchema, ModelMeasureRefSchema, RatioRefSchema]))
    .min(1)
    .max(5)
    .describe('De uma a cinco métricas: id do catálogo, {mestre} / {agregacao, campo} de describe_app.modelo, ou {razao: {numerador, denominador}}'),
  group_by: z
    .array(z.union([IdSchema, ModelDimensionRefSchema]))
    .max(2)
    .default([])
    .describe('Até duas dimensões de agrupamento: id do catálogo, ou {campo, grain?} de describe_app.modelo'),
  period: QueryPeriodSchema.optional(),
  filters: QueryFiltersSchema,
  exclude: z
    .record(DimensionOrFieldSchema, z.array(z.string().min(1).max(200)).min(1).max(20))
    .default({})
    .describe('Valores a tirar, por id de dimensão ou nome de campo do modelo'),
  sort: z
    .object({
      by: z
        .string()
        .min(1)
        .max(256)
        .describe('Id de uma métrica ou dimensão da consulta, nome do campo do modelo agrupado, id da medida mestra ou id de coluna de um resultado'),
      direction: SortDirectionSchema.default('desc'),
    })
    .optional()
    .describe('Ordenação. Sem ela: a maior métrica primeiro, ou ordem cronológica quando há grão de período'),
  limit: z.number().int().min(1).max(1000).default(50),
});
export type QueryInput = z.infer<typeof QueryInputSchema>;

// ---------- explain_result ----------

export const ExplainResultInputSchema = z.object({
  result_id: ResultIdSchema.describe('Id do resultado (result_id) devolvido antes'),
});
export type ExplainResultInput = z.infer<typeof ExplainResultInputSchema>;

export const ExplainResultOutputSchema = z.object({
  result_id: ResultIdSchema,
  executed_at: z.string(),
  subject: z.object({ id: IdSchema, nome: z.string(), donos: z.object({ negocio: z.string(), tecnico: z.string() }) }),
  query: z.object({
    id: IdSchema,
    versao: z.number().int(),
    exemplo: z.string(),
    certificada_por: z.string().nullable(),
  }),
  metricas: z.array(z.object({ id: IdSchema, nome: z.string(), descricao: z.string(), unidade: z.string(), versao: z.number().int() })),
  params: z.record(z.string(), z.string()),
  expressions_executed: z.array(z.string()),
  publication: z.object({ id: PublicationIdSchema, commit: z.string().nullable() }),
  interpretation: z.object({ prompt_version: z.string(), model_id: z.string() }).nullable(),
  versions: z.object({ lastrouter: z.string(), adapter: z.string() }),
  source: z.object({ id: IdSchema, app: z.string(), last_reload_at: z.string().nullable() }),
});
export type ExplainResultOutput = z.infer<typeof ExplainResultOutputSchema>;

// ---------- resolve_question (contrato 4) ----------

export const ResolveQuestionInputSchema = z.object({
  question: z
    .string()
    .min(1)
    .max(1000)
    .describe('Pergunta autônoma, já reescrita pelo agente (sem depender da conversa anterior)'),
  previous_result_id: ResultIdSchema.optional().describe('Resultado anterior, para reaproveitar a query e trocar só parâmetros'),
  clarification: z.string().min(1).max(200).optional().describe('Opção escolhida pelo usuário depois de needs_clarification'),
});
export type ResolveQuestionInput = z.infer<typeof ResolveQuestionInputSchema>;

export const ResolveAnsweredSchema = z.object({
  status: z.literal('answered'),
  query_id: IdSchema,
  params: z.record(z.string(), z.string()),
  result: QueryResultSchema,
});

export const ResolveClarificationSchema = z.object({
  status: z.literal('needs_clarification'),
  question: z.string(),
  options: z.array(z.string()),
  /** Parâmetro que ficou em aberto, quando há um. */
  parameter: z.string().optional(),
  query_id: IdSchema.optional(),
});

export const ResolveNoAnswerSchema = z.object({
  status: z.literal('no_answer'),
  message: z.string(),
});

export const ResolveQuestionOutputSchema = z.discriminatedUnion('status', [
  ResolveAnsweredSchema,
  ResolveClarificationSchema,
  ResolveNoAnswerSchema,
]);
export type ResolveQuestionOutput = z.infer<typeof ResolveQuestionOutputSchema>;

// ---------- report_feedback (contrato 6) ----------

export const FeedbackKindSchema = z.enum(['wrong', 'review', 'useful']);
export type FeedbackKind = z.infer<typeof FeedbackKindSchema>;

export const ReportFeedbackInputSchema = z.object({
  result_id: ResultIdSchema.describe('Resultado sobre o qual o usuário deu retorno'),
  kind: FeedbackKindSchema.describe('wrong: resposta errada; review: pedir revisão ao dono do assunto; useful: útil'),
  comment: z.string().max(2000).optional().describe('O que o usuário disse, resumido'),
});
export type ReportFeedbackInput = z.infer<typeof ReportFeedbackInputSchema>;

export const ReportFeedbackOutputSchema = z.object({
  received: z.literal(true),
  feedback_id: z.string(),
  routed_to: z.array(z.string()),
});
export type ReportFeedbackOutput = z.infer<typeof ReportFeedbackOutputSchema>;

// ---------- propose_context_change ----------

export const ProposeContextChangeInputSchema = z.object({
  question: z.string().min(1).max(1000).describe('O que o usuário escreveu ao corrigir, como ele escreveu. Fica registrado na auditoria e no rascunho'),
  subject_id: IdSchema.describe('Id do app, como devolvido por list_apps'),
  section: SectionKeySchema.default('correcoes').describe('Seção do contexto que recebe a correção. Padrão: correcoes'),
  text: z.string().trim().min(1).max(2000).describe('A correção como regra, numa frase completa. Ex.: "Chamado aberto não inclui os pendentes."'),
  original_question: z.string().max(1000).optional().describe('A pergunta que o usuário fez antes e que a correção corrige, quando houver'),
});
export type ProposeContextChangeInput = z.infer<typeof ProposeContextChangeInputSchema>;

export const ProposeContextChangeOutputSchema = z.object({
  status: z.literal('pending_approval'),
  subject_id: IdSchema,
  section: SectionKeySchema,
  /** Número da versão em rascunho que recebeu a correção. */
  draft_version: z.number().int(),
  /** Quem precisa aprovar antes da publicação. */
  approvers: z.array(z.object({ papel: z.enum(['negocio', 'tecnico']), owner: z.string() })),
  message: z.string(),
});
export type ProposeContextChangeOutput = z.infer<typeof ProposeContextChangeOutputSchema>;

// ---------- erros ----------

export const ToolErrorCodeSchema = z.enum([
  'unauthorized',
  'forbidden',
  'not_found',
  'invalid_parameter',
  'value_not_found',
  'rate_limited',
  'busy',
  'source_unavailable',
  'timeout',
  'interpretation_unavailable',
  'internal',
]);
export type ToolErrorCode = z.infer<typeof ToolErrorCodeSchema>;

/** Regra 7: mensagem genérica e id de correlação; o detalhe fica só no log. */
export const ToolErrorSchema = z.object({
  error: z.object({
    code: ToolErrorCodeSchema,
    message: z.string(),
    correlation_id: z.string(),
    retry_after_seconds: z.number().int().optional(),
  }),
});
export type ToolErrorPayload = z.infer<typeof ToolErrorSchema>;
